/**
 * Chart State — Tracked state and setters for chart configuration.
 *
 * Extracted from ProgramsPage to encapsulate all chart-view specific state
 * (field selection, chart type, date grouping, time range, etc.) in a
 * single object that can be instantiated, serialised, and tested independently.
 */
import { tracked } from '@glimmer/tracking';
import type { ChartType } from 'tio-common/types/chart';
import type { DateGroupingInterval } from './date-grouping';

export type { ChartType };
export type DateGrouping = DateGroupingInterval;

/** Serialisable snapshot of chart configuration (for saving/loading reports). */
export interface ChartConfig {
  selectedField: string;
  selectedType: ChartType;
  summaryTitle: string;
  dateGrouping: DateGrouping;
  showDataTable: boolean;
  selectedDataset: string;
  selectedTimeRange: string;
}

const DEFAULTS: ChartConfig = {
  selectedField: 'tasApplicationStateKeyword',
  selectedType: 'bar',
  summaryTitle: '',
  dateGrouping: 'day',
  showDataTable: false,
  selectedDataset: 'tas-program-instances',
  selectedTimeRange: 'all',
};

export class ChartState {
  @tracked selectedField = DEFAULTS.selectedField;
  @tracked selectedType: ChartType = DEFAULTS.selectedType;
  @tracked summaryTitle = DEFAULTS.summaryTitle;
  @tracked dateGrouping: DateGrouping = DEFAULTS.dateGrouping;
  @tracked showDataTable = DEFAULTS.showDataTable;
  @tracked selectedDataset = DEFAULTS.selectedDataset;
  @tracked selectedTimeRange = DEFAULTS.selectedTimeRange;

  // -------------------------------------------------------------------
  // Setters (arrow functions for stable identity when passed as callbacks)
  // -------------------------------------------------------------------

  setField = (field: string) => {
    this.selectedField = field;
  };

  setType = (type: ChartType) => {
    this.selectedType = type;
  };

  setDateGrouping = (dateGrouping: DateGrouping) => {
    this.dateGrouping = dateGrouping;
  };

  setShowDataTable = (show: boolean) => {
    this.showDataTable = show;
  };

  setDataset = (dataset: string) => {
    this.selectedDataset = dataset;
  };

  setTimeRange = (timeRange: string) => {
    this.selectedTimeRange = timeRange;
  };

  setSummaryTitle = (title: string) => {
    this.summaryTitle = title;
  };

  // -------------------------------------------------------------------
  // Serialisation
  // -------------------------------------------------------------------

  /** Snapshot current state into a plain object suitable for JSON persistence. */
  toConfig(): ChartConfig {
    return {
      selectedField: this.selectedField,
      selectedType: this.selectedType,
      summaryTitle: this.summaryTitle,
      dateGrouping: this.dateGrouping,
      showDataTable: this.showDataTable,
      selectedDataset: this.selectedDataset,
      selectedTimeRange: this.selectedTimeRange,
    };
  }

  /** Apply a previously-saved configuration snapshot. */
  fromConfig(config: Partial<ChartConfig>) {
    if (config.selectedField !== undefined) this.selectedField = config.selectedField;
    if (config.selectedType !== undefined) this.selectedType = config.selectedType;
    if (config.summaryTitle !== undefined) this.summaryTitle = config.summaryTitle;
    if (config.dateGrouping !== undefined) this.dateGrouping = config.dateGrouping;
    if (config.showDataTable !== undefined) this.showDataTable = config.showDataTable;
    if (config.selectedDataset !== undefined) this.selectedDataset = config.selectedDataset;
    if (config.selectedTimeRange !== undefined) this.selectedTimeRange = config.selectedTimeRange;
  }

  /** Reset all chart state to defaults. */
  reset() {
    this.selectedField = DEFAULTS.selectedField;
    this.selectedType = DEFAULTS.selectedType;
    this.summaryTitle = DEFAULTS.summaryTitle;
    this.dateGrouping = DEFAULTS.dateGrouping;
    this.showDataTable = DEFAULTS.showDataTable;
    this.selectedDataset = DEFAULTS.selectedDataset;
    this.selectedTimeRange = DEFAULTS.selectedTimeRange;
  }
}
